import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Droplets, Star, Crown, Award, ChevronRight, Gift, Ticket, Car, Bike, CreditCard, Calendar, X, CheckCircle } from 'lucide-react';
import SystemPayForm from '../components/SystemPayForm';
import { redirectToSystemPay } from '../lib/systempayRedirect';

const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED !== 'false';
const SUBSCRIBE_LABEL = PAYMENTS_ENABLED ? 'Acheter en ligne' : 'Reserver (paiement en magasin)';

const LAVAGE_PRESTATIONS = [
  {
    id: 'velo',
    name: 'VÉLO',
    price: 12,
    icon: Droplets,
    color: 'text-green-400',
    bgColor: 'from-green-600/20',
    features: ['Lavage cadre', 'Nettoyage transmission', 'Rinçage complet'],
    popular: false,
    image: 'https://images.pexels.com/photos/12944977/pexels-photo-12944977.jpeg',
    vehicleTypes: ['velo']
  },
  {
    id: 'clean',
    name: 'CLEAN',
    price: 25,
    icon: Droplets,
    color: 'text-blue-400',
    bgColor: 'from-blue-600/20',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus'],
    popular: false,
    image: 'https://images.unsplash.com/photo-1761312834150-4beefff097a7?w=400',
    vehicleTypes: ['auto', 'moto']
  },
  {
    id: 'vip',
    name: 'VIP',
    price: 55,
    icon: Star,
    color: 'text-yellow-400',
    bgColor: 'from-yellow-600/20',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum'],
    popular: true,
    image: 'https://images.pexels.com/photos/6872582/pexels-photo-6872582.jpeg',
    vehicleTypes: ['auto']
  },
  {
    id: 'platine',
    name: 'PLATINE',
    price: 75,
    icon: Crown,
    color: 'text-purple-400',
    bgColor: 'from-purple-600/20',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum', 'Shampoing tapis'],
    popular: false,
    image: 'https://images.unsplash.com/photo-1760550818554-f4990528bd5a?w=400',
    vehicleTypes: ['auto']
  },
  {
    id: 'prestige',
    name: 'PRESTIGE',
    price: 115,
    icon: Award,
    color: 'text-red-500',
    bgColor: 'from-red-600/20',
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum', 'Shampoing tapis', 'Plastiques nourris et ravivés', 'Cire brillant professionnel', 'Lustrage', 'Protection contre UV, pluie et saletés'],
    popular: false,
    image: 'https://images.unsplash.com/photo-1770064747044-0b45514b7ec4?w=400',
    vehicleTypes: ['auto']
  },
];

export default function LavagePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [paymentData, setPaymentData] = useState(null); // { formToken, subscriptionId, planName, price }
  const [paymentStatus, setPaymentStatus] = useState(null);
  const paymentSectionRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const API = API_URL + '/api';

  useEffect(() => {
    if (!paymentData) return;
    setTimeout(() => {
      paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, [paymentData]);

  const handlePaid = useCallback(async () => {
    if (!paymentData) return;
    try {
      await fetch(`${API}/subscriptions/confirm?subscription_id=${paymentData.subscriptionId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
    setPaymentStatus('success');
    toast.success('Abonnement active !');
  }, [paymentData, API, token]);

  const handleFailed = useCallback(() => setPaymentStatus('error'), []);

  const cancelPayment = () => {
    setPaymentData(null);
    setPaymentStatus(null);
  };

  const handleBuySubscription = async (planId, planName, price) => {
    if (!user) {
      toast.error('Veuillez vous connecter pour acheter');
      navigate('/connexion');
      return;
    }

    setLoadingPlan(planId);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          plan_name: planName,
          price: price,
          origin_url: window.location.origin
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la création du paiement');
      }
      const data = await res.json();
      if (data.skip_payment) {
        // Mode paiement desactive : abonnement enregistre, paiement en magasin
        setPaymentData({ formToken: null, subscriptionId: data.subscriptionId, planName, price });
        setPaymentStatus('success');
        toast.success('Demande enregistree ! A regler en magasin.');
      } else if (data.redirect_form) {
        // Mode redirection SystemPay
        redirectToSystemPay(data.redirect_form);
      } else if (data.formToken) {
        setPaymentData({
          formToken: data.formToken,
          subscriptionId: data.subscriptionId,
          planName,
          price
        });
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors du paiement');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full border border-blue-600/30 mb-6">
            <Droplets size={18} />
            <span className="text-sm font-medium">Nos prestations de lavage</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            TARIFS <span className="text-red-500">LAVAGE</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-6">
            Choisissez la formule adaptée à vos besoins pour un véhicule impeccable
          </p>
          <Link to="/reservation-lavage">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3">
              <Calendar className="mr-2" size={20} />
              Réserver un créneau
            </Button>
          </Link>
        </div>
      </section>

      {/* Offre Fidélité Banner */}
      <section className="py-6 bg-gradient-to-r from-green-900/30 via-green-600/20 to-green-900/30 border-y border-green-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
                <Gift className="text-white" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">OFFRE FIDÉLITÉ</h3>
                <p className="text-green-400 text-lg font-semibold">10 lavages achetés = 2 OFFERTS</p>
                <p className="text-zinc-400 text-sm">Selon la formule choisie • Cumulable sur votre carte fidélité</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-zinc-800/80 rounded-lg p-4 text-center">
                <Ticket className="text-green-400 mx-auto mb-2" size={24} />
                <p className="text-white font-bold">Achetez 10</p>
                <p className="text-zinc-500 text-xs">lavages</p>
              </div>
              <ChevronRight className="text-green-400" size={24} />
              <div className="bg-green-600/20 border border-green-500/50 rounded-lg p-4 text-center">
                <Gift className="text-green-400 mx-auto mb-2" size={24} />
                <p className="text-green-400 font-bold">Recevez 12</p>
                <p className="text-zinc-500 text-xs">lavages</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prestations */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {LAVAGE_PRESTATIONS.map((prestation, idx) => (
              <Card 
                key={prestation.id}
                className={`relative bg-zinc-900/50 border-zinc-800 overflow-hidden card-hover animate-fade-in ${
                  prestation.popular ? 'border-yellow-500/50 ring-1 ring-yellow-500/30' : ''
                }`}
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`prestation-card-${prestation.id}`}
              >
                {prestation.popular && (
                  <div className="absolute top-0 right-0 bg-yellow-500 text-black text-xs font-bold px-3 py-1 z-10">
                    POPULAIRE
                  </div>
                )}
                
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={prestation.image} 
                    alt={prestation.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                </div>
                
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <div className={`w-14 h-14 mx-auto rounded-full bg-zinc-800 flex items-center justify-center mb-3 -mt-10 relative z-10 border-2 border-zinc-700`}>
                      <prestation.icon className={prestation.color} size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-white">{prestation.name}</h3>
                    {/* Vehicle type icons */}
                    <div className="flex items-center justify-center gap-2 mt-2">
                      {prestation.vehicleTypes?.includes('auto') && (
                        <div className="flex items-center gap-1 bg-zinc-800 px-2 py-1 rounded-full">
                          <Car size={14} className="text-red-500" />
                          <span className="text-xs text-zinc-400">Auto</span>
                        </div>
                      )}
                      {prestation.vehicleTypes?.includes('moto') && (
                        <div className="flex items-center gap-1 bg-zinc-800 px-2 py-1 rounded-full">
                          <Bike size={14} className="text-red-500" />
                          <span className="text-xs text-zinc-400">Moto</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-white">{prestation.price}€</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 mb-4">
                    {prestation.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-zinc-300 text-xs">
                        <Check className="text-green-500 flex-shrink-0" size={14} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to={`/reservation-lavage?formula=${prestation.id}`}>
                    <Button 
                      className={`w-full ${
                        prestation.popular 
                          ? 'bg-yellow-500 hover:bg-yellow-600 text-black btn-glow' 
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                      data-testid={`book-${prestation.id}`}
                    >
                      Réserver un créneau
                      <ChevronRight size={18} className="ml-1" />
                    </Button>
                  </Link>
                  <a href="#abonnements" className="block mt-2">
                    <Button 
                      variant="outline"
                      className="w-full border-green-500 text-green-400 hover:bg-green-500/10 font-bold"
                    >
                      <Gift size={16} className="mr-2" />
                      Abonnement -17%
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Formules Abonnement */}
      <section id="abonnements" className="py-16 bg-gradient-to-b from-zinc-900/50 to-[#09090B] border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-600/20 text-green-400 px-4 py-2 rounded-full border border-green-600/30 mb-6">
              <Gift size={18} />
              <span className="text-sm font-medium">Offre Fidélité</span>
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">
              FORMULES <span className="text-green-400">ABONNEMENT</span>
            </h2>
            <p className="text-xl text-zinc-400">10 lavages achetés = 2 OFFERTS • Économisez jusqu'à 230€</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {/* VÉLO Abonnement */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-green-500/30 overflow-hidden">
              <div className="bg-green-600 text-white text-center py-2 text-sm font-bold">
                2 LAVAGES OFFERTS
              </div>
              <CardContent className="p-6 text-center">
                <Droplets className="text-green-400 mx-auto mb-3" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">VÉLO x12</h3>
                <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages vélo</p>
                <div className="mb-4">
                  <span className="text-zinc-500 line-through text-lg">150€</span>
                  <p className="text-4xl font-bold text-green-400">125€</p>
                  <span className="text-zinc-500 text-sm">soit 10.42€/lavage</span>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3 mb-4">
                  <p className="text-green-400 font-semibold">Économie : 25€</p>
                </div>
                <Button 
                  onClick={() => handleBuySubscription('abo-velo', 'VÉLO x12', 125)}
                  disabled={loadingPlan === 'abo-velo'}
                  className="w-full bg-green-600 hover:bg-green-700 text-lg py-6 font-bold btn-glow"
                  data-testid="buy-abo-velo"
                >
                  <CreditCard size={20} className="mr-2" />
                  {loadingPlan === 'abo-velo' ? 'Chargement...' : SUBSCRIBE_LABEL}
                </Button>
              </CardContent>
            </Card>

            {/* CLEAN Abonnement */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-blue-500/30 overflow-hidden">
              <div className="bg-blue-600 text-white text-center py-2 text-sm font-bold">
                2 LAVAGES OFFERTS
              </div>
              <CardContent className="p-6 text-center">
                <Droplets className="text-blue-400 mx-auto mb-3" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">CLEAN x12</h3>
                <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages CLEAN</p>
                <div className="mb-4">
                  <span className="text-zinc-500 line-through text-lg">300€</span>
                  <p className="text-4xl font-bold text-blue-400">250€</p>
                  <span className="text-zinc-500 text-sm">soit 20.83€/lavage</span>
                </div>
                <div className="bg-blue-500/10 rounded-lg p-3 mb-4">
                  <p className="text-blue-400 font-semibold">Économie : 50€</p>
                </div>
                <Button 
                  onClick={() => handleBuySubscription('abo-clean', 'CLEAN x12', 250)}
                  disabled={loadingPlan === 'abo-clean'}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6 font-bold btn-glow"
                  data-testid="buy-abo-clean"
                >
                  <CreditCard size={20} className="mr-2" />
                  {loadingPlan === 'abo-clean' ? 'Chargement...' : SUBSCRIBE_LABEL}
                </Button>
              </CardContent>
            </Card>

            {/* VIP Abonnement */}
            <Card className="bg-zinc-900/50 border-yellow-500/50 ring-2 ring-yellow-500/30 overflow-hidden">
              <div className="bg-yellow-500 text-black text-center py-2 text-sm font-bold">
                ⭐ BEST SELLER - 2 OFFERTS
              </div>
              <CardContent className="p-6 text-center">
                <Star className="text-yellow-400 mx-auto mb-3" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">VIP x12</h3>
                <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages VIP</p>
                <div className="mb-4">
                  <span className="text-zinc-500 line-through text-lg">660€</span>
                  <p className="text-4xl font-bold text-yellow-400">550€</p>
                  <span className="text-zinc-500 text-sm">soit 45.83€/lavage</span>
                </div>
                <div className="bg-yellow-500/10 rounded-lg p-3 mb-4">
                  <p className="text-yellow-400 font-semibold">Économie : 110€</p>
                </div>
                <Button 
                  onClick={() => handleBuySubscription('abo-vip', 'VIP x12', 550)}
                  disabled={loadingPlan === 'abo-vip'}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black text-lg py-6 font-bold btn-glow animate-pulse"
                  data-testid="buy-abo-vip"
                >
                  <CreditCard size={20} className="mr-2" />
                  {loadingPlan === 'abo-vip' ? 'Chargement...' : SUBSCRIBE_LABEL}
                </Button>
              </CardContent>
            </Card>

            {/* PLATINE Abonnement */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-purple-500/30 overflow-hidden">
              <div className="bg-purple-600 text-white text-center py-2 text-sm font-bold">
                2 LAVAGES OFFERTS
              </div>
              <CardContent className="p-6 text-center">
                <Crown className="text-purple-400 mx-auto mb-3" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">PLATINE x12</h3>
                <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages PLATINE</p>
                <div className="mb-4">
                  <span className="text-zinc-500 line-through text-lg">900€</span>
                  <p className="text-4xl font-bold text-purple-400">750€</p>
                  <span className="text-zinc-500 text-sm">soit 62.50€/lavage</span>
                </div>
                <div className="bg-purple-500/10 rounded-lg p-3 mb-4">
                  <p className="text-purple-400 font-semibold">Économie : 150€</p>
                </div>
                <Button 
                  onClick={() => handleBuySubscription('abo-platine', 'PLATINE x12', 750)}
                  disabled={loadingPlan === 'abo-platine'}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6 font-bold btn-glow"
                  data-testid="buy-abo-platine"
                >
                  <CreditCard size={20} className="mr-2" />
                  {loadingPlan === 'abo-platine' ? 'Chargement...' : SUBSCRIBE_LABEL}
                </Button>
              </CardContent>
            </Card>

            {/* PRESTIGE Abonnement */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-red-500/30 overflow-hidden">
              <div className="bg-red-600 text-white text-center py-2 text-sm font-bold">
                2 LAVAGES OFFERTS
              </div>
              <CardContent className="p-6 text-center">
                <Award className="text-red-500 mx-auto mb-3" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">PRESTIGE x12</h3>
                <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages PRESTIGE</p>
                <div className="mb-4">
                  <span className="text-zinc-500 line-through text-lg">1380€</span>
                  <p className="text-4xl font-bold text-red-500">1150€</p>
                  <span className="text-zinc-500 text-sm">soit 95.83€/lavage</span>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3 mb-4">
                  <p className="text-red-400 font-semibold">Économie : 230€</p>
                </div>
                <Button 
                  onClick={() => handleBuySubscription('abo-prestige', 'PRESTIGE x12', 1150)}
                  disabled={loadingPlan === 'abo-prestige'}
                  className="w-full bg-red-600 hover:bg-red-700 text-lg py-6 font-bold btn-glow"
                  data-testid="buy-abo-prestige"
                >
                  <CreditCard size={20} className="mr-2" />
                  {loadingPlan === 'abo-prestige' ? 'Chargement...' : SUBSCRIBE_LABEL}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-zinc-500 text-sm">
              Les cartes sont valables 1 an • Non remboursables • Utilisables sur tous nos véhicules (auto, moto, vélo)
            </p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-zinc-900/30 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">INFORMATIONS</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6">
                <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Droplets className="text-blue-400" size={20} />
                  Types de véhicules
                </h4>
                <p className="text-zinc-400">
                  Nos prestations sont disponibles pour tous types de véhicules : voitures, SUV, utilitaires, motos et scooters.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6">
                <h4 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Star className="text-yellow-400" size={20} />
                  Qualité garantie
                </h4>
                <p className="text-zinc-400">
                  Nous utilisons des produits professionnels de haute qualité pour un résultat impeccable à chaque lavage.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-zinc-400 mb-6">
              Besoin d'un service personnalisé ? Contactez-nous pour un devis sur mesure.
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/reservation">
                <Button size="lg" className="bg-red-600 hover:bg-red-700">
                  Prendre RDV
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-zinc-600 text-white hover:bg-zinc-800">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section paiement inline (affichée quand l'utilisateur a cliqué Acheter) */}
      {paymentData && (
        <section ref={paymentSectionRef} className="py-12 bg-gradient-to-b from-zinc-900 to-[#09090B] border-y-2 border-red-600/30" data-testid="subscription-payment-section">
          <div className="max-w-2xl mx-auto px-4">
            {paymentStatus === 'success' ? (
              <Card className="bg-zinc-900/50 border-green-500/50">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {paymentData.formToken ? 'Abonnement active !' : 'Demande enregistree !'}
                  </h2>
                  <p className="text-zinc-400 mb-6">
                    {paymentData.formToken
                      ? `Votre carte ${paymentData.planName} est desormais active.`
                      : `Votre demande pour ${paymentData.planName} a bien ete enregistree. Le paiement (${Number(paymentData.price).toFixed(2)} EUR) se fera en magasin.`}
                  </p>
                  <Button onClick={() => navigate('/mon-compte')} className="bg-red-600 hover:bg-red-700 w-full" data-testid="view-account-btn">
                    Voir mon espace
                  </Button>
                </CardContent>
              </Card>
            ) : paymentStatus === 'error' ? (
              <Card className="bg-zinc-900/50 border-red-500/50">
                <CardContent className="p-8 text-center">
                  <X className="mx-auto text-red-500 mb-4" size={64} />
                  <h2 className="text-2xl font-bold text-white mb-2">Échec du paiement</h2>
                  <p className="text-zinc-400 mb-6">Veuillez réessayer.</p>
                  <Button onClick={cancelPayment} variant="outline" className="border-zinc-700 w-full" data-testid="retry-payment-btn">
                    Retour aux abonnements
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <CreditCard className="text-red-500" size={32} /> PAIEMENT
                  </h2>
                  <Button onClick={cancelPayment} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="cancel-payment-btn">
                    Annuler
                  </Button>
                </div>

                <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-lg text-white font-bold">{paymentData.planName}</p>
                        <p className="text-sm text-zinc-400">Abonnement</p>
                      </div>
                      <p className="text-3xl text-red-500 font-bold">{Number(paymentData.price).toFixed(2)} €</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-6">
                    <SystemPayForm
                      formToken={paymentData.formToken}
                      amount={Number(paymentData.price || 0)}
                      onPaid={handlePaid}
                      onFailed={handleFailed}
                      testId="systempay-sub-form"
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
